import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CustomerObjectServiceCallCommentDto } from '../../../shared/models/customer-object-service-calls/customer-object-service-call-comment.dto';
import { SecurityService } from '../../../core/services/security.service';
import { CustomerObjectServiceCallsService } from '../customer-object-service-calls.service';
import { NotificationService } from '../../../core/services/notification.service';
import { utcApiValueToLocalDate } from '../../../shared/pipes/utc-date-format.pipe';

@Component({
	selector: 'customer-object-service-call-comments',
	templateUrl: './customer-object-service-call-comments.component.html',
	styleUrls: ['./customer-object-service-call-comments.component.scss',
		'../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
		'../../../../vendor/libs/angular2-ladda/angular2-ladda.scss'
	]
})
export class CustomerObjectServiceCallCommentsComponent {

	@Input() comments: CustomerObjectServiceCallCommentDto[] = [];
	@Input() customerObjectId: number;
	@Input() commentPrefix: string;
	@Input() serviceCallFinalised: boolean = false;
	@Input() showPostponeFields: boolean = false;

	@Output() onCommentAdded: EventEmitter<void> = new EventEmitter<void>();

	newCommentText: string = '';
	postponeServiceCallDateTime: Date = null;
	postponeForever: boolean = false;
	isSending: boolean = false;

	get minPostponeDateTime(): Date {
		return new Date();
	}

	get isPostponeDateTimeInPast(): boolean {
		return !!this.postponeServiceCallDateTime && this.postponeServiceCallDateTime < new Date();
	}

	get isPostponeDateTimeInvalid(): boolean {
		return this.showPostponeFields
			&& !this.postponeForever
			&& (!this.postponeServiceCallDateTime || this.isPostponeDateTimeInPast);
	}

	get isCommentInvalid(): boolean {
		return this.showPostponeFields && !(this.newCommentText && this.newCommentText.trim());
	}

	constructor(
		private securityService: SecurityService,
		private customerObjectServiceCallsService: CustomerObjectServiceCallsService,
		private notificationService: NotificationService
	) { }

	get currentUserId(): number {
		return this.securityService.currentUser.userId;
	}

	getAuthorLabel(comment: CustomerObjectServiceCallCommentDto): string {
		return comment.createdUserId === this.currentUserId ? 'Я' : (comment.createdUserName || '');
	}

	getAuthorInfo(comment: CustomerObjectServiceCallCommentDto): string {
		let result = '';
		if (comment.createdUserGroupName) {
			result += comment.createdUserGroupName;
		}
		if (comment.createdUserServiceCenterName) {
			result += result.length > 0 ? ' ' : '';
			result += `(${comment.createdUserServiceCenterName})`;
		}
		return result;
	}

	getDateText(date: Date | string): string {
		if (!date) return '';
		const d = utcApiValueToLocalDate(date);
		if (!d) return '';
		const today = new Date();
		const isToday = d.getDate() === today.getDate()
			&& d.getMonth() === today.getMonth()
			&& d.getFullYear() === today.getFullYear();

		const hours = d.getHours().toString().padStart(2, '0');
		const minutes = d.getMinutes().toString().padStart(2, '0');

		if (isToday) {
			return `Сегодня в ${hours}:${minutes}`;
		}

		const day = d.getDate().toString().padStart(2, '0');
		const month = (d.getMonth() + 1).toString().padStart(2, '0');
		return `${day}.${month}.${d.getFullYear()} в ${hours}:${minutes}`;
	}

	addComment(): void {
		if (!this.newCommentText.trim() || this.isSending) return;
		if (this.showPostponeFields && !this.postponeForever && !this.postponeServiceCallDateTime) {
			this.notificationService.warning({ title: 'Отложить', message: 'Укажите дату и время отложения' });
			return;
		}
		if (this.showPostponeFields && !this.postponeForever && this.isPostponeDateTimeInPast) {
			this.notificationService.warning({ title: 'Отложить', message: 'Дата и время отложения не могут быть меньше текущих' });
			return;
		}
		
		let commentText = this.commentPrefix != null
			? this.commentPrefix + this.newCommentText
			: this.newCommentText;

		this.isSending = true;
		this.customerObjectServiceCallsService.createComment(
			this.customerObjectId,
			commentText,
			this.serviceCallFinalised,
			this.showPostponeFields ? this.postponeServiceCallDateTime : null,
			this.showPostponeFields && this.postponeForever
		)
			.subscribe(resp => {
				this.isSending = false;
				if (resp.isSuccessful) {
					this.newCommentText = '';
					this.postponeServiceCallDateTime = null;
					this.postponeForever = false;
					this.onCommentAdded.emit();
				}
			}, () => {
				this.isSending = false;
			});
	}

	onPostponeForeverClick(event: Event): void {
		const checkbox = event.target as HTMLInputElement;

		if (this.postponeForever) {
			this.postponeForever = false;
			checkbox.checked = false;
			return;
		}

		checkbox.checked = false;

		this.notificationService.confirmation({
			message: 'Этот объект будет исключен из списка на сервисное обслуживание навсегда. Вы уверены?',
			confirmButtonText: 'Да',
			cancelButtonText: 'Нет'
		}, () => {
			this.postponeForever = true;
			checkbox.checked = true;
			this.postponeServiceCallDateTime = null;
		}, () => {
			this.postponeForever = false;
			checkbox.checked = false;
		});
	}

	onKeyDown(event: KeyboardEvent): void {
		if (event.ctrlKey && event.key === 'Enter') {
			event.preventDefault();
			this.addComment();
		}
	}
}
